/**
 * Express Checkout Shared Utilities
 * Common functions used across all Express Checkout contexts
 */

import { showSectionAlert } from '../core/alerts.js';

/**
 * Show error message
 * @param {string} message - Error message
 */
export function showError(message) {
    const errorDiv = document.getElementById('checkout-errors');

    if (errorDiv) {
        showSectionAlert(errorDiv, message, 'error');
        errorDiv.style.display = 'block';
        errorDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
        // Create a temporary accessible alert container if no error div exists
        let tempContainer = document.getElementById('express-checkout-errors');

        if (!tempContainer) {
            tempContainer = document.createElement('div');
            tempContainer.id = 'express-checkout-errors';
            tempContainer.setAttribute('aria-live', 'assertive');

            const section = document.querySelector('[data-mcom="express-checkout"]');

            if (section) {
                section.prepend(tempContainer);
            } else {
                document.body.prepend(tempContainer);
            }
        }

        showSectionAlert(tempContainer, message, 'error');
        tempContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
}

/**
 * Update order totals in the UI
 * @param {Object} result - Result object with tax and total information
 */
export function updateTotals(result) {
    if (result.taxFormatted) {
        const taxElement = document.querySelector('[data-mcom="order-tax"]');
        if (taxElement) {
            taxElement.textContent = result.taxFormatted;
        }

        const taxRow = document.querySelector('[data-mcom="tax-total-row"]');
        if (taxRow) {
            taxRow.style.display = '';
        }
    }

    if (result.shippingAmountFormatted) {
        const shippingElement = document.querySelector('[data-mcom="order-shipping"]');
        if (shippingElement) {
            shippingElement.textContent = result.shippingAmountFormatted;
        }

        const shippingRow = document.querySelector('[data-mcom="shipping-total-row"]');
        if (shippingRow) {
            shippingRow.style.display = '';
        }
    }

    if (result.orderTotalFormatted) {
        const totalElement = document.querySelector('[data-mcom="order-total"]');
        if (totalElement) {
            totalElement.textContent = result.orderTotalFormatted;
        }

        const estimatedTotalElement = document.querySelector('[data-mcom="order-estimated-total"]');
        if (estimatedTotalElement) {
            estimatedTotalElement.textContent = result.orderTotalFormatted;
        }

        const estimatedTotalRow = document.querySelector('[data-mcom="estimated-total-row"]');
        if (estimatedTotalRow) {
            estimatedTotalRow.style.display = '';
        }
    }

    const footer = document.querySelector('[data-mcom="cart-summary-footer"]');
    if (footer) {
        if (result.shippingAmountFormatted && result.taxFormatted) {
            footer.textContent = 'Totals calculated based on current shipping selection.';
        } else if (result.shippingAmountFormatted) {
            footer.textContent = 'Tax calculated at checkout.';
        } else if (result.taxFormatted) {
            footer.textContent = 'Shipping fees calculated at checkout.';
        }
    }

    // Announce total update to screen readers via the checkout status live region
    if (result.orderTotalFormatted) {
        const statusRegion = document.querySelector('[data-mcom="checkout-status"]');
        if (statusRegion) {
            statusRegion.textContent = `Order total updated to ${result.orderTotalFormatted}`;
        }
    }
}

/**
 * Get CSRF token for API requests
 * @returns {string|null} - CSRF token or null
 */
export function getCsrfToken() {
    return document.querySelector('input[name=__RequestVerificationToken]')?.value;
}

/**
 * Parse cart items from JSON string
 * @param {string} cartItemsJson - JSON string of cart items
 * @returns {Array} - Parsed cart items array
 */
export function parseCartItems(cartItemsJson) {
    try {
        return JSON.parse(cartItemsJson || '[]');
    } catch (error) {
        console.error('Error parsing cart items:', error);
        return [];
    }
}

/**
 * Calculate total amount in cents from cart items
 * @param {Array} cartItems - Array of cart items
 * @returns {number} - Total amount in cents
 */
export function calculateTotalAmount(cartItems) {
    return cartItems.reduce((sum, item) => {
        return sum + (item.unitAmount * item.quantity);
    }, 0);
}
